'use client';

import type { PickOption } from '@inixiative/rules-builder';
import { ComboboxSelect, ItemIcon } from '@zealot/ui/components/combobox-select';
import { Dialog, DialogContent, DialogTitle } from '@zealot/ui/components/dialog';
import { Input } from '@zealot/ui/components/input';
import { cn } from '@zealot/ui/lib/utils';
import { ChevronDown, Plus, Search, X } from 'lucide-react';
import { useContext, useDeferredValue, useMemo, useState } from 'react';
import {
  availableContainers,
  BuilderContext,
  commitDeferred,
  fieldDecorIcon,
  fieldItems,
  relabelElementFields,
} from './lib';

// gloss
export const FieldPickerDialog = ({
  selected,
  options,
  onSelect,
  onClose,
}: {
  selected: string | null | undefined;
  options: readonly PickOption[];
  onSelect: (name: string) => void;
  onClose: () => void;
}) => {
  const { groups, rootLabel, rootIcon, decoration, source, elementModel } = useContext(BuilderContext);
  const valueHome = selected
    ? (groups.containerOf.get(selected) ?? (groups.containers.some((c) => c.id === selected) ? selected : ''))
    : '';
  const [browsing, setBrowsing] = useState<string>(valueHome ?? '');
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const query = deferredSearch.trim().toLowerCase();

  const sources = useMemo(
    () => [{ id: '', label: rootLabel, icon: rootIcon }, ...groups.containers],
    [groups, rootLabel, rootIcon],
  );

  const homeOf = (value: string) => groups.containerOf.get(value) ?? '';

  const countOf = useMemo(() => {
    const counts = new Map<string, number>();
    for (const option of options) counts.set(homeOf(option.value), (counts.get(homeOf(option.value)) ?? 0) + 1);
    return counts;
  }, [options, groups]);

  const sourceLabelOf = useMemo(() => {
    const labels = new Map(groups.containers.map((container) => [container.id, container.label]));
    labels.set('', rootLabel);
    return labels;
  }, [groups, rootLabel]);

  const within = useMemo(() => {
    const pool = query ? options : options.filter((option) => homeOf(option.value) === browsing);
    return query ? pool.filter((option) => option.label.toLowerCase().includes(query)) : pool;
  }, [options, groups, browsing, query]);

  const iconOf = useMemo(() => {
    const byContainer = new Map(sources.map((entry) => [entry.id, entry.icon]));
    const model = elementModel ?? source?.model;
    return (option: PickOption) =>
      option.icon ?? fieldDecorIcon(decoration, model, option.value) ?? byContainer.get(homeOf(option.value));
  }, [sources, groups, decoration, source, elementModel]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="full" hideClose className="flex h-[620px] max-h-[88vh] max-w-4xl gap-0 overflow-hidden p-0">
        <div className="flex w-56 flex-none flex-col border-border border-r bg-muted/40">
          <div className="border-border border-b px-4 py-3.5">
            <DialogTitle className="text-sm">Data Sources</DialogTitle>
            <p className="mt-0.5 text-muted-foreground text-xs">Select a platform to view fields</p>
          </div>
          <div className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
            {sources.map((source) => (
              <button
                key={source.id}
                type="button"
                onClick={() => {
                  setBrowsing(source.id);
                  setSearch('');
                }}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
                  browsing === source.id && !query
                    ? 'bg-background font-medium shadow-sm'
                    : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
                )}
              >
                {source.icon ? <ItemIcon icon={source.icon} label={source.label} /> : null}
                <span className="min-w-0 flex-1 truncate">{source.label}</span>
                <span className="font-mono text-muted-foreground text-xs">{countOf.get(source.id) ?? 0}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-3 border-border border-b px-5 py-3.5">
            <div className="relative flex-1">
              <Search className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-muted-foreground" />
              <Input
                aria-label="Search fields"
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search fields…"
                className="pl-9"
              />
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="flex size-8 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5">
            {within.length ? (
              <div className="grid grid-cols-2 content-start gap-3">
                {within.map((option) => {
                  const icon = iconOf(option);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onSelect(option.value)}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary hover:bg-primary/5',
                        option.value === selected && 'border-primary bg-primary/5',
                      )}
                    >
                      <span className="flex size-9 flex-none items-center justify-center overflow-hidden rounded-lg bg-primary/10 text-primary">
                        {icon ? (
                          <ItemIcon icon={icon} label={option.label} />
                        ) : (
                          <span className="font-semibold text-sm">{option.label.charAt(0).toUpperCase()}</span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-sm">{option.label}</span>
                        {}
                        {query ? (
                          <span className="block truncate text-muted-foreground text-xs">
                            {sourceLabelOf.get(homeOf(option.value))}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                {}
                {query ? 'No fields match your search' : 'No segmentable fields from this source yet'}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// gloss
export const FieldPickerControl = ({
  selected,
  options,
  set,
}: {
  selected: string | null | undefined;
  options: readonly PickOption[];
  set: (name: string) => void;
}) => {
  const { groups, rootIcon, decoration, source, elementModel } = useContext(BuilderContext);
  const [isOpen, setIsOpen] = useState(false);
  const current = selected ? options.find((o) => o.value === selected) : undefined;
  const currentIcon = current
    ? (current.icon ??
      fieldDecorIcon(decoration, elementModel ?? source?.model, current.value) ??
      (groups.facetIds.has(current.value) ? undefined : rootIcon))
    : undefined;

  return (
    <>
      <button
        type="button"
        data-slot="field-picker"
        onClick={() => setIsOpen(true)}
        className={cn(
          'flex h-9 min-w-36 flex-1 items-center gap-2 rounded-md border bg-card px-2.5 text-left text-sm transition-colors hover:border-primary',
          current ? 'border-border' : 'border-dashed text-muted-foreground hover:text-primary',
        )}
      >
        {current ? (
          <>
            {currentIcon ? <ItemIcon icon={currentIcon} label={current.label} /> : null}
            <span className="truncate">{current.label}</span>
          </>
        ) : (
          <>
            <Plus className="size-3.5 flex-none" />
            Select condition…
          </>
        )}
        <ChevronDown className="ml-auto size-3.5 flex-none text-muted-foreground" />
      </button>
      {isOpen ? (
        <FieldPickerDialog
          selected={selected}
          options={options}
          onSelect={(name) => {
            commitDeferred(() => set(name));
            setIsOpen(false);
          }}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
};

// gloss
export const FacetFieldPicker = ({
  selected,
  options: rawOptions,
  set,
}: {
  selected: string | null | undefined;
  options: readonly PickOption[];
  set: (name: string) => void;
}) => {
  const { groups, rootLabel, rootIcon, sentence, decoration, elementModel } = useContext(BuilderContext);
  const [browsing, setBrowsing] = useState<string | null>(null);
  const options = useMemo(() => {
    return relabelElementFields(rawOptions, decoration, elementModel).filter(
      (option) => !option.hidden || option.value === selected,
    );
  }, [rawOptions, decoration, elementModel, selected]);
  const pick = set;

  if (sentence && !elementModel) return <FieldPickerControl selected={selected} options={options} set={pick} />;

  const containers = availableContainers(groups, options);

  if (!containers.length) {
    return (
      <ComboboxSelect
        items={fieldItems(options)}
        value={selected ?? null}
        onValueChange={(next) => next && commitDeferred(() => pick(next))}
        placeholder="field"
        className="w-56"
      />
    );
  }

  const valueHome = selected
    ? (groups.containerOf.get(selected) ?? (containers.some((c) => c.id === selected) ? selected : ''))
    : null;
  const browsed = browsing ?? valueHome ?? '';
  const shown = containers.some((container) => container.id === browsed) ? browsed : '';

  const within =
    shown === ''
      ? options.filter((o) => !groups.facetIds.has(o.value))
      : options.filter((o) => groups.containerOf.get(o.value) === shown);

  return (
    <>
      <ComboboxSelect
        items={[
          { value: '', label: rootLabel, icon: rootIcon },
          ...containers.map(({ id, label, icon }) => ({ value: id, label, icon })),
        ]}
        value={shown}
        onValueChange={(next) => setBrowsing(next ?? '')}
        placeholder="facet"
        className="w-44"
      />
      <ComboboxSelect
        items={fieldItems(within)}
        value={selected && within.some((o) => o.value === selected) ? selected : null}
        onValueChange={(next) => next && commitDeferred(() => pick(next))}
        placeholder="field"
        className="w-52"
      />
    </>
  );
};
